<template>
    <div class="audio-player" :class="{ 'is-playing': playing, 'is-hidden': hidden }" @click="toggle">
        <div class="audio-player-disc">
            <img v-if="cover" class="audio-player-cover" :src="cover" alt="">
            <div v-else class="audio-player-inner"></div>
        </div>
        <div class="audio-player-icon">
            <svg v-if="playing" viewBox="0 0 24 24" width="18" height="18">
                <path d="M6 4h4v16H6zM14 4h4v16h-4z" fill="#fff"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" width="18" height="18">
                <path d="M8 5v14l11-7z" fill="#fff"/>
            </svg>
        </div>
        <audio ref="audio"
                :src="src || defaultSrc"
                :loop="loop"
                :autoplay="autoplay"
                preload="auto"
                @play="onPlay"
                @pause="onPause"
                @ended="onEnded"></audio>
    </div>
</template>
<script>
const DEFAULT_BGM = 'https://res.wx.qq.com/voice/getvoice?mediaid=MzU3MTcxMjQ3Ml8xMDAwMDY4NjI=&voice_type=1'

export default {
    name: 'AudioPlayer',
    props: {
        src: {
            type: String,
            default: '',
        },
        cover: {
            type: String,
            default: '',
        },
        loop: {
            type: Boolean,
            default: true,
        },
        autoplay: {
            type: Boolean,
            default: true,
        },
        hidden: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        return {
            playing: false,
            defaultSrc: DEFAULT_BGM,
            firstInteractionBound: false,
        }
    },
    mounted () {
        if (this.autoplay) {
            this.$nextTick(() => {
                this.tryPlay()
            })
        }
    },
    beforeDestroy () {
        this.unbindFirstInteraction()
        const audio = this.$refs.audio
        if (audio) {
            audio.pause()
            audio.src = ''
        }
    },
    methods: {
        tryPlay () {
            const audio = this.$refs.audio
            if (!audio) return
            const p = audio.play()
            if (p && typeof p.then === 'function') {
                p.catch(() => {
                    this.bindFirstInteraction()
                    if (typeof wx !== 'undefined' && document.addEventListener) {
                        document.addEventListener('WeixinJSBridgeReady', this.resumeByBridge, { once: true })
                    }
                })
            }
        },
        resumeByBridge () {
            const audio = this.$refs.audio
            if (!audio) return
            const p = audio.play()
            if (p && typeof p.then === 'function') {
                p.catch(() => this.bindFirstInteraction())
            }
        },
        bindFirstInteraction () {
            if (this.firstInteractionBound) return
            this.firstInteractionBound = true
            const handler = () => {
                this.unbindFirstInteraction()
                const audio = this.$refs.audio
                if (audio) audio.play().catch(() => {})
            }
            this._firstInteractionHandler = handler
            document.addEventListener('touchstart', handler, { once: true, passive: true })
            document.addEventListener('click', handler, { once: true })
        },
        unbindFirstInteraction () {
            if (!this.firstInteractionBound) return
            this.firstInteractionBound = false
            const handler = this._firstInteractionHandler
            if (!handler) return
            document.removeEventListener('touchstart', handler)
            document.removeEventListener('click', handler)
            this._firstInteractionHandler = null
        },
        toggle () {
            const audio = this.$refs.audio
            if (!audio) return
            if (audio.paused) {
                audio.play().catch(() => {})
            } else {
                audio.pause()
            }
        },
        onPlay () {
            this.playing = true
            this.$emit('play')
        },
        onPause () {
            this.playing = false
            this.$emit('pause')
        },
        onEnded () {
            this.playing = false
            this.$emit('ended')
        },
    },
}
</script>
<style lang="scss" scoped>
.audio-player {
    position: fixed;
    top: 50%;
    left: 24px;
    right: auto;
    transform: translate(0, calc(-50% - 50px));
    width: 88px;
    height: 88px;
    z-index: 9998;
    cursor: pointer;
    user-select: none;
    -webkit-tap-highlight-color: transparent;

    .audio-player-disc {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: radial-gradient(circle at 50% 50%, #333 0 18%, #111 18% 60%, #222 60% 100%);
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35), inset 0 0 0 2px rgba(255, 255, 255, 0.08);
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .audio-player-cover {
        width: 54%;
        height: 54%;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid rgba(255, 255, 255, 0.12);
        background: #222;
    }

    .audio-player-inner {
        width: 28%;
        height: 28%;
        border-radius: 50%;
        background: radial-gradient(circle at 50% 50%, #555 0 55%, #2b2b2b 55% 100%);
        box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.06);
    }

    .audio-player-icon {
        position: absolute;
        bottom: -4px;
        right: -4px;
        width: 34px;
        height: 34px;
        border-radius: 50%;
        background: #ff7e1c;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 10px rgba(255, 126, 28, 0.4);
    }

    &.is-playing .audio-player-disc {
        animation: audio-player-spin 6s linear infinite;
    }

    &.is-hidden {
        opacity: 0;
        pointer-events: none;
    }
}

@keyframes audio-player-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
</style>
